import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import Histogram from '#app/components/Histogram'
import LineChart from '#app/components/LineChart.tsx'
import NavigableTable from '#app/components/NavigableTable'
import RankTable from '#app/components/RankTable'
import {
	extractWeeklyRank,
	formatTime,
	getHistogramData,
	getPeerWindow,
	getResultsById,
	getRunnerById,
} from '#app/queries/RankingAllData-queries'
import { capitalize } from '#app/utils/stringHelpers.ts'
import FlippableCard from '#app/components/FlippableCard.tsx'

export async function loader({ params }: LoaderFunctionArgs) {
	try {
		const { id } = params
		const runner = await getRunnerById(id as string)
		const [peerWindow, histogramData, results] = await Promise.all([
			getPeerWindow(runner),
			getHistogramData(runner.ageClass, runner.sex),
			getResultsById(runner.athlete),
		])

		const formattedResults = results.map(result => ({
			...result,
			time: formatTime(result.time),
		}))

		const weeklyRank = extractWeeklyRank(runner.athleteLink.Ranking?.weeklyRank || '')
		return json({ runner, peerWindow, histogramData, results: formattedResults, weeklyRank })
	} catch {
		throw json({ error: 404, statusText: 'Could not load athlete' })
	}
}

export default function Athlete() {
	const { runner, histogramData, peerWindow, results, weeklyRank } = useLoaderData<typeof loader>()

	function isHighlighted(entry: any) {
		return entry?.['fullName'] === runner.fullName
	}

	const competitorMap = {
		Name: 'fullName',
		Age: 'ageClass',
		Time: 'formattedTime',
	}

	const resultsMap = {
		Race: 'raceName',
		Time: 'time',
	}

	return (
		<div className="flex justify-center">
			<div className="flex max-w-[700px] flex-1 flex-col items-center gap-y-4">
				<FlippableCard
					FrontComponent={
						<>
							<div className="mb-2 text-center text-h3">{capitalize(runner?.fullName)}</div>
							{histogramData && (
								<Histogram data={histogramData} runner={runner} color={'#EAB308'} />
							)}
							<div className="w-full pt-2 text-center">
								<label>
									Fastest Time
									<div className="text-body-xl">{formatTime(runner.time)}</div>
								</label>
							</div>
						</>
					}
					BackComponent={
						<div className="flex flex-col gap-y-2">
							<div>
								<span className="font-bold underline">This historgram</span> represents the bucketed
								distribution of runners marathon times across the runner's given age and sex.
							</div>
							<div>
								<span className="font-bold underline">The bucket</span> highlighted in yellow
								represents where the selected runner exists in the overall distribution.
							</div>
							<div>
								The further to the right a runner is, the faster they are relative to their peers.
							</div>
						</div>
					}
				/>
				<FlippableCard
					FrontComponent={<RankTable data={runner} includePercent={true} />}
					BackComponent={
						<div>
							<ul className="flex flex-col gap-y-2">
								<li>
									<span className="font-bold underline">Top % Effort All Time:</span> The ranking of
									a marathon time (effort) vs all time efforts. A ranking of top 30% indicates a
									regional class effort, top 20% a national class effort and top 10% a world class
									effort.
								</li>
								<li>
									<span className="font-bold underline">Division Ranking:</span> The ranking of an
									athlete's effort within all efforts of their sex and age grouping.
								</li>
								<li>
									<span className="font-bold underline">Overall Ranking:</span> The overall ranking
									of an effort with respect to only time.
								</li>
							</ul>
						</div>
					}
				/>
				<FlippableCard
					FrontComponent={<LineChart title="Change in Weekly Rank" data={weeklyRank} />}
					BackComponent={
						<div>
							<span className="font-bold underline">Change in Weekly Rank</span> indicates how an
							athlete's ranking has changed over time. Generally, this will be a downward trend as
							faster times come in throughout the year, but an athlete has the opportunity to
							improve their ranking by posting another faster time.
						</div>
					}
				/>
				<NavigableTable
					mapping={resultsMap}
					// label="Results"
					isHighlighted={() => false}
					data={results}
				/>
				<NavigableTable
					mapping={competitorMap}
					// label="Closest Competitors"
					isHighlighted={isHighlighted}
					columnToHighlight="fullName"
					data={peerWindow}
				/>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
